// meet — pure utilities. Row-shape transforms used by fetch blocks (allowed
// imports per the chat pattern), bucket constants, and the room-mode helper.

// Source of truth for the four AI suggestion buckets. Used by:
//   - meet-analyze Edge Function (the LLM prompt + responseSchema)
//   - meet_transcript_analyses.suggestions JSONB keys
//   - applySuggestion dispatcher
//   - SuggestionCard rendering
// Adding a fifth bucket is a one-line change here + the Edge Function schema.
// `label` / `viewLabel` carry polyglot KEYS — every render site resolves them
// through `el.call('polyglot', …)`.
export const SUGGESTION_BUCKETS = {
  calendar_events: {
    label: 'meet.bucket.calendar',
    viewLabel: 'meet.bucket.calendarView',
    stateKey: 'selectedEventId',
    applyFn: 'saveEvent',
    route: '/calendar'
  },
  tickets: {
    label: 'meet.bucket.tickets',
    viewLabel: 'meet.bucket.ticketsView',
    stateKey: 'selectedTicketId',
    applyFn: 'saveTicket',
    route: '/tasks'
  },
  notes: {
    label: 'meet.bucket.notes',
    viewLabel: 'meet.bucket.notesView',
    stateKey: 'selectedNoteId',
    applyFn: 'createNote',
    route: '/notes'
  },
  knowledge_base: {
    label: 'meet.bucket.knowledgeBase',
    viewLabel: 'meet.bucket.knowledgeBaseView',
    stateKey: 'selectedDocumentId',
    applyFn: 'saveKbArticle',
    route: '/notes/knowledge-base'
  },
  chat_messages: {
    label: 'meet.bucket.chat',
    viewLabel: 'meet.bucket.chatView',
    applyFn: 'messageUser',
    route: '/chat'
  }
}

// Mode is derived from participant count — never persist it as state.
// dm < 3 < grid < 13 ≤ stage. Used by VideoRoom layout AND livekitConnect's
// simulcast layer choice.
export const getRoomMode = (participantCount) => {
  if (participantCount < 3) return 'dm'
  if (participantCount < 13) return 'grid'
  return 'stage'
}

export const rowToRoom = (r) => ({
  id: r.id,
  // A polyglot KEY when the row has no name — every render site resolves
  // `room.name` through polyglot, and translate() passes a real name through.
  name: r.name || 'meet.roomList.untitled',
  topic: r.topic || '',
  kind: r.kind || 'room',
  // visibility supersedes isPrivate (3-state: private/workspace/public).
  // Migration 0149 backfilled from is_private; rows from older clients
  // that didn't set visibility default to 'workspace' at the DB level.
  // isPrivate is kept synced for legacy readers — drop after consumers migrate.
  visibility: r.visibility || (r.is_private ? 'private' : 'workspace'),
  isPrivate: !!r.is_private,
  transcribeEnabled: !!r.transcribe_enabled,
  livekitInitialized: !!r.livekit_initialized,
  createdBy: r.created_by,
  createdAt: r.created_at,
  endedAt: r.ended_at,
  calendarEventId: r.calendar_event_id || null,
  chatChannelId: r.chat_channel_id || null,
  // Guest-join requirements (migration 0019). `guestRequireName` defaults
  // to true; the others default false/null. Read by NewRoomModal /
  // MeetRoomSettingsModal / /meet-guest page.
  guestRequireName: r.guest_require_name !== false,
  guestRequireEmail: !!r.guest_require_email,
  guestPassword: r.guest_password || null,
  guestAutoAdmit: !!r.guest_auto_admit
})

export const rowToParticipant = (r) => ({
  id: r.id,
  roomId: r.room_id,
  userId: r.user_id,
  identity: r.identity,
  joinedAt: r.joined_at,
  leftAt: r.left_at
})

export const rowToUtterance = (r) => ({
  id: r.id,
  roomId: r.room_id,
  userId: r.user_id,
  // The transcriber writes `speaker_name` as whatever LiveKit's
  // `participant.name` is, which in our case is the user's EMAIL (set by
  // meet-token when minting the token). Keep it stored as-is; components
  // resolve the display name via root.people at render time.
  speakerEmail: r.speaker_name || '',
  text: r.text || '',
  startedAt: r.started_at,
  endedAt: r.ended_at,
  confidence: r.confidence
})

// Dedupe + chronologically order utterances. ONE canonical pass shared by
// every utterance writer (the SSE append in subscribeMeetRealtime, the
// transcriptView fetch transform, the localStorage first-paint) so no
// entry path can reintroduce dupes. Two dupe classes, both observed live
// (2026-07-29 per Nika "it is grabbing a transcript, but there is a lot of
// duped messages"):
//
//   1. SAME id delivered twice — an SSE reconnect replay, or the realtime
//      append racing the RPC fetch window (the fetch replaces state with
//      rows the append already added). Exact id match.
//   2. DISTINCT ids, same content — more than one transcriber worker
//      registered against the same LiveKit project each transcribing the
//      room (the pre-keepalive production-slug worker alongside the dev
//      one — tickets/server.md SERVER-MEET-TRANSCRIBER "will re-zombie"
//      note). Each worker inserts its own row: different id, identical
//      text. Collapsed CONSERVATIVELY: same speaker + identical non-empty
//      trimmed text + started_at within 2s — a real "yes. yes." repeat is
//      two different utterance windows and survives.
export const dedupeUtterances = (list) => {
  const rows = (Array.isArray(list) ? list : []).filter((u) => u && u.id)
  rows.sort((a, b) => {
    const ta = Date.parse(a.startedAt) || 0
    const tb = Date.parse(b.startedAt) || 0
    return ta - tb || String(a.id).localeCompare(String(b.id))
  })
  const out = []
  const seenIds = new Set()
  for (const u of rows) {
    const id = String(u.id)
    if (seenIds.has(id)) continue
    seenIds.add(id)
    const text = (u.text || '').trim()
    const t = Date.parse(u.startedAt) || 0
    let crossWorkerDupe = false
    if (text) {
      // Only the tail can collide (sorted by time; 2s window) — check the
      // last few kept rows, break as soon as we're past the window.
      for (let i = out.length - 1; i >= 0 && i >= out.length - 6; i--) {
        const p = out[i]
        const pt = Date.parse(p.startedAt) || 0
        if (t - pt > 2000) break
        if ((p.speakerEmail || '') === (u.speakerEmail || '') && (p.text || '').trim() === text) {
          crossWorkerDupe = true
          break
        }
      }
    }
    if (!crossWorkerDupe) out.push(u)
  }
  return out
}

// Resolve a speaker to a human display name by looking up root.people
// (populated via peopleFetch from shared-gov). The speaker's id first: the
// people list hides a peer's e-mail from a non-manager (PeopleService's
// reduced projection), so a caption keyed by e-mail alone showed the raw
// address to editors and clients (dev 2026-10-23). A transcript row's
// user_id is the speaker's LiveKit identity, which is the entry's userId.
// Falls back to the e-mail (or free-form speaker string) if no match.
export const speakerDisplayName = (root, speakerEmail, userId) => {
  const people = root?.people || []
  const byId = userId
    ? people.find((p) => p && p.userId && String(p.userId) === String(userId))
    : null
  if (byId && byId.name) return byId.name
  if (!speakerEmail) return 'meet.transcript.unknownSpeaker'
  const match = people.find((p) => p.email === speakerEmail)
  return match?.name || speakerEmail
}

// Find the routing root by walking up the parent chain to the first element
// that owns the `routes` prop (i.e. the Page element that mounts pages/index).
// Needed because DOMQL v3.14 doesn't expose `el.getRoot()` — existing components
// like AppNavbar use this walk pattern, and calling `el.router(path, undef)`
// throws `Cannot read properties of undefined (reading 'context')`.
export const routeTo = (el, path) => {
  if (!el || !path) return
  let root = el
  while (root && !root.routes) root = root.parent
  if (root) el.router(path, root)
}

// Content-addressable id for a suggestion item — used by applied_items so
// regenerating the analysis doesn't break checkmarks. Truncated sha256 of
// title + description; collision risk is negligible at the scales involved.
export const fingerprintSuggestion = async (item) => {
  const text = `${item.title || ''}|${item.description || item.content || item.body || ''}`
  const buf = new TextEncoder().encode(text)
  const hash = await crypto.subtle.digest('SHA-256', buf)
  const bytes = new Uint8Array(hash).slice(0, 8)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

// Strip applied_items entries whose fingerprints don't appear in the current
// suggestions blob. Called by applySuggestion before appending the new entry
// so the array stays bounded by the most recent analysis, not lifetime clicks.
export const pruneStaleApplied = async (appliedItems, suggestions) => {
  if (!appliedItems?.length) return []
  const liveFingerprints = new Set()
  for (const bucket of Object.keys(SUGGESTION_BUCKETS)) {
    for (const item of suggestions?.[bucket] || []) {
      liveFingerprints.add(await fingerprintSuggestion(item))
    }
  }
  return appliedItems.filter((entry) => liveFingerprints.has(entry.fingerprint))
}

// Resolve which participant should be featured on the stage right now.
// Single source of truth shared by MeetFeaturedTile (visibility + video
// source) and applyTileGridLayout (filmstrip mode + hiding the featured
// participant's duplicate strip tile). Returns the participant object
// from livekitParticipants, or null when there's nothing meaningful to
// show (0 or 1 participant total — everyone fits in the grid).
// Priority: active screen-share → active speaker (prefer non-local) →
// room host.
// GRID-FIRST featuring (2026-06-12 per Nika "make tiles as grid…put
// expand button on tile so it highlights on bigger"): auto-staging is
// gone — the grid is the default view and the stage appears only for
//   1. a user PIN (root.meetPinnedTileId, set by each tile's expand
//      button; the stage's collapse button clears it), or
//   2. an active screen-share.
// Speakers are highlighted in place via SpeakerRing, never auto-staged
// (the cam-off auto-stage was the "empty box in the top"; even cam-on
// auto-stage churned the layout on every speaker change).
// Layout modes (root.meetLayoutMode, persisted as 'meet:layout'):
//   'grid'    (default) — everyone equal; the stage appears only for a
//             user pin or a screen-share.
//   'speaker' — FaceTime/Zoom speaker view: the stage follows whoever is
//             talking (avatar stage when their camera is off — fine,
//             the user chose spotlighting), host as the quiet fallback.
export const resolveFeaturedParticipant = (s) => {
  const participants = s.root?.livekitParticipants || []
  if (participants.length <= 1) return null

  const pinnedId = s.root?.meetPinnedTileId
  if (pinnedId) {
    const pinned = participants.find((p) => (p.id || p.identity) === pinnedId)
    if (pinned) return pinned
    // Pinned participant left — fall through (stale pin is harmless;
    // the next pin overwrites it).
  }

  const screen = participants.find((p) => p.isScreen)
  if (screen) return screen

  if (s.root?.meetLayoutMode === 'speaker') {
    const speakers = s.root?.livekitSpeakers || {}
    const speaking = Object.keys(speakers)
      .filter((id) => speakers[id])
      .map((id) => participants.find((pp) => pp.identity === id && !pp.isScreen))
      .filter(Boolean)
    const speakerParticipant = speaking.find((p) => !p.isLocal) || speaking[0]
    if (speakerParticipant) return speakerParticipant
    const room = (s.root?.rooms || []).find((r) => r.id === s.root?.activeRoomId)
    const host = participants.find((p) => p.identity === room?.createdBy && !p.isScreen)
    if (host) return host
  }

  return null
}

// ── Call-failure classification ──────────────────────────────────────────
//
// MEET-CALL-SERVER-UNREACHABLE-1. The error overlay used to select its copy
// by matching the raw SDK message with /signal connection|websocket/i, which
// discriminates nothing: a transient edge blip, a refused credential and an
// exhausted service quota all produce a "websocket"/"signal connection"
// message, and all three rendered "Connection hiccup" with a Retry button.
//
// livekit-client 2.21.0 already knows better than that message. Every
// `ConnectionError` carries the edge's own verdict — `reasonName` plus the
// HTTP status LiveKit answered its /rtc/validate probe with. Where the status
// sits depends on the variant (`errors.d.ts`):
//   * notAllowed / websocket / serverUnreachable → `err.status`
//   * internal                                   → `err.context.status`
// ⚠️ `err.code` is NOT a discriminator: `LivekitError` gives EVERY
// ConnectionError code 1. Reading it as one is how this stayed unclassified.
//
// Measured 2026-08-21 against both LiveKit projects (symbols-test-kkb05lze
// and symbols-prod-xxjhh3ym): /rtc/validate answers HTTP 429 "bandwidth usage
// exceeded. please contact the project owner." — the account quota, not a
// network fault and not a credential fault. Retry cannot clear it. That is
// the case this classifier exists to stop mislabelling.
//
// Copy contract: the `transient` branch keeps the exact wording settled by
// MEET-ERROR-COPY-LIES-1 — do not drift it, and do not let any branch assert
// a cause the status has not established.
//
// @param {object} e - the flattened error fields recorded on root state by
//   connectCall (`meetCallError`, `meetCallErrorName`, `meetCallErrorCode`,
//   `meetCallErrorReason`, `meetCallErrorStatus`).
// Returns polyglot KEYS, never copy: the classifier is pure and unit-tested
// off the DOM, so the RENDER SITE resolves `titleKey`/`hintKey` and fills
// `params` (VideoRoom's error overlay). `hintKey` is '' when there is no hint.
//
// @returns {{kind:string,titleKey:string,hintKey:string,params:object,retryable:boolean}}
export const classifyCallError = (e = {}) => {
  const message = e.message || ''
  const status = typeof e.status === 'number' ? e.status : null

  if (!message && status === null) {
    return { kind: 'none', titleKey: '', hintKey: '', params: {}, retryable: false }
  }

  // The service itself refused the join with a status. These are FACTS from
  // the edge, so they may be named — and none of them clears on Retry.
  if (status === 429) {
    return {
      kind: 'capacity',
      titleKey: 'meet.error.capacityTitle',
      hintKey: 'meet.error.capacityHint',
      params: { status: 429 },
      retryable: false
    }
  }
  if (status === 401 || status === 403) {
    return {
      kind: 'denied',
      titleKey: 'meet.error.forbiddenTitle',
      hintKey: 'meet.error.forbiddenHint',
      params: { status },
      retryable: false
    }
  }
  if (status === 404) {
    return {
      kind: 'missing',
      titleKey: 'meet.error.missingTitle',
      hintKey: 'meet.error.missingHint',
      params: { status },
      retryable: false
    }
  }

  if (/livekit config missing/i.test(message)) {
    return {
      kind: 'unconfigured',
      titleKey: 'meet.error.configTitle',
      hintKey: 'meet.error.configHint',
      params: {},
      retryable: false
    }
  }

  // No status: the connection never got an answer to classify. This is the
  // only branch that may honestly say "hiccup" and offer Retry.
  if (/signal connection|websocket/i.test(message)) {
    return {
      kind: 'transient',
      titleKey: 'meet.error.networkTitle',
      hintKey: 'meet.error.networkHint',
      params: {},
      retryable: true
    }
  }

  return {
    kind: 'unknown',
    titleKey: 'meet.error.unknownTitle',
    hintKey: '',
    params: { message },
    retryable: true
  }
}

// Flatten a thrown livekit-client error into the plain fields the meet root
// state carries (state must stay serialisable — no Error instances in it).
//
// Reads the status from BOTH places livekit-client can put it: `err.status`
// on the notAllowed / websocket / serverUnreachable variants, and
// `err.context.status` on the internal variant — which is the one a 429
// quota rejection arrives as. Nothing here infers or classifies; it only
// carries over fields the error object already has.
export const livekitErrorFields = (err) => ({
  message: err?.message || 'connect failed',
  name: err?.name ?? null,
  // Kept for logs only. Every ConnectionError has code 1 — see
  // classifyCallError; never branch on it.
  code: err?.code ?? null,
  reasonName: err?.reasonName ?? null,
  status:
    typeof err?.status === 'number'
      ? err.status
      : typeof err?.context?.status === 'number'
        ? err.context.status
        : null
})
